import * as React from 'react';
import { ArrowUp, Loader2, Sparkles } from 'lucide-react';
import { Modal } from './modal';
import { Button } from '@/components/ui/button';
import { inputBase } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import type { PageDetail } from '@/lib/types';

// =====================================================================
// AI chat modal (3B-3.5e).
//
// Multi-turn conversation. Parent (page-edit) provides `onSubmit` that
// POSTs the full message history to /api/.../ai-chat and returns the
// server's next turn: either a new assistant question ('asking') or
// the generated page ('done'). On 'done' the modal closes and the
// parent applies the page via onPageGenerated.
// =====================================================================

export interface AiChatWireMessage {
  role: 'user' | 'assistant';
  content: string;
}

export type AiTurnResult =
  | { state: 'asking'; assistantMessage: string }
  | { state: 'done'; assistantMessage: string; page: PageDetail; generationId: string }
  // `gate` (WP1 Task 12): an optional UpgradeGate element the caller has
  // already built (page-edit.tsx composes it with the dashboard UpgradeGate
  // wrapper) — this component just renders whatever node it's given below
  // the error bubble, so it stays free of a dependency on that component.
  | { state: 'error'; message: string; gate?: React.ReactNode };

export interface AiChatModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (messages: AiChatWireMessage[]) => Promise<AiTurnResult>;
  // Called once the AI's done generating; parent hands the new page
  // off to its onPageReplaced flow (modal closes itself first).
  onPageGenerated: (page: PageDetail) => void;
}

type Role = 'assistant' | 'user';
interface ChatMessage {
  id: string;
  role: Role;
  content: string;
  gate?: React.ReactNode;
}

const INITIAL_GREETING: ChatMessage = {
  id: 'greeting',
  role: 'assistant',
  content:
    "Hi! Tell me what your business does, where you are, and who you serve. I'll ask a couple of quick questions, then write the page.",
};

const MIN_PROMPT_LENGTH = 4;

export function AiChatModal({ open, onClose, onSubmit, onPageGenerated }: AiChatModalProps) {
  const [messages, setMessages] = React.useState<ChatMessage[]>([INITIAL_GREETING]);
  const [draft, setDraft] = React.useState('');
  const [submitting, setSubmitting] = React.useState(false);
  const textareaRef = React.useRef<HTMLTextAreaElement | null>(null);
  const scrollRef = React.useRef<HTMLDivElement | null>(null);

  // Reset state each time the modal opens.
  React.useEffect(() => {
    if (open) {
      setMessages([INITIAL_GREETING]);
      setDraft('');
      setSubmitting(false);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open]);

  // Auto-scroll messages to bottom as the conversation grows.
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [messages, submitting]);

  async function send() {
    const trimmed = draft.trim();
    if (trimmed.length < MIN_PROMPT_LENGTH || submitting) return;

    // Append user message locally and compute the wire history. The
    // initial greeting is NOT sent to the server — it's a UI-only
    // priming bubble; the server's system prompt covers the same
    // framing. Wire messages start at the first real user turn.
    const userMsg: ChatMessage = {
      id: `u-${Date.now()}`,
      role: 'user',
      content: trimmed,
    };
    const nextMessages = [...messages, userMsg];
    setMessages(nextMessages);
    setDraft('');
    setSubmitting(true);

    const wire: AiChatWireMessage[] = nextMessages
      .filter((m) => m.id !== 'greeting')
      .map((m) => ({ role: m.role, content: m.content }));

    try {
      const result = await onSubmit(wire);
      if (result.state === 'asking') {
        setMessages((m) => [
          ...m,
          { id: `a-${Date.now()}`, role: 'assistant', content: result.assistantMessage },
        ]);
        requestAnimationFrame(() => textareaRef.current?.focus());
      } else if (result.state === 'done') {
        // Show the assistant's closing message briefly, then close and
        // hand the new page to the parent.
        setMessages((m) => [
          ...m,
          {
            id: `a-${Date.now()}`,
            role: 'assistant',
            content: result.assistantMessage,
          },
        ]);
        setTimeout(() => onPageGenerated(result.page), 700);
      } else {
        setMessages((m) => [
          ...m,
          { id: `a-${Date.now()}`, role: 'assistant', content: result.message, gate: result.gate },
        ]);
        requestAnimationFrame(() => textareaRef.current?.focus());
      }
    } finally {
      setSubmitting(false);
    }
  }

  function onKey(e: React.KeyboardEvent<HTMLTextAreaElement>) {
    if (!submitting && (e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      void send();
    }
  }

  const canSend = !submitting && draft.trim().length >= MIN_PROMPT_LENGTH;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Let AI build your page"
      description="A short conversation, then AI writes the whole page. About 30 seconds."
      titleBadge={<Sparkles className="h-4 w-4" />}
      size="3xl"
      disableClose={submitting}
      footer={
        <>
          <textarea
            ref={textareaRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={onKey}
            rows={2}
            disabled={submitting}
            placeholder="e.g. I run a hair salon in Mission Hills, San Diego — color and balayage, walk-ins welcome."
            className={cn(inputBase, 'resize-none py-2 leading-relaxed')}
            aria-label="Reply to the AI"
          />
          <div className="mt-2 flex items-center justify-between gap-3">
            <span className="text-xs text-text-tertiary">
              Uses 1 of your monthly AI generations when the page is written.
            </span>
            {/* B4: outline until the draft is long enough to send. While
                submitting, `canSend` is false but the button keeps its fill
                (T8: never change variant mid-action). */}
            <Button
              type="button"
              size="sm"
              variant={submitting || draft.trim().length >= MIN_PROMPT_LENGTH ? 'default' : 'outline'}
              onClick={() => void send()}
              disabled={!canSend}
            >
              {submitting ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Thinking…
                </>
              ) : (
                <>
                  Send
                  <ArrowUp className="h-3.5 w-3.5" />
                </>
              )}
            </Button>
          </div>
        </>
      }
    >
      <div ref={scrollRef} className="flex h-full flex-col justify-end space-y-3">
        {messages.map((m) => (
          <React.Fragment key={m.id}>
            <Bubble role={m.role}>{m.content}</Bubble>
            {m.gate ? <div className="pl-1">{m.gate}</div> : null}
          </React.Fragment>
        ))}
        {submitting ? <TypingBubble /> : null}
      </div>
    </Modal>
  );
}

function Bubble({ role, children }: { role: Role; children: React.ReactNode }) {
  const isUser = role === 'user';
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] whitespace-pre-line rounded-2xl px-4 py-2.5 text-sm leading-relaxed',
          isUser
            ? 'bg-primary text-primary-foreground rounded-br-sm'
            : 'bg-surface-sunken text-foreground rounded-bl-sm',
        )}
      >
        {children}
      </div>
    </div>
  );
}

function TypingBubble() {
  return (
    <div className="flex justify-start">
      <div className="inline-flex items-center gap-1 rounded-2xl rounded-bl-sm bg-surface-sunken px-4 py-3 text-sm">
        <span className="size-1.5 animate-[typing-pulse_1.2s_ease-in-out_infinite] rounded-full bg-text-tertiary [animation-delay:-0.3s]" />
        <span className="size-1.5 animate-[typing-pulse_1.2s_ease-in-out_infinite] rounded-full bg-text-tertiary [animation-delay:-0.15s]" />
        <span className="size-1.5 animate-[typing-pulse_1.2s_ease-in-out_infinite] rounded-full bg-text-tertiary" />
      </div>
    </div>
  );
}
